import React from 'react';
import { Box } from '@atlaskit/primitives';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { boxStyles } from '../../constants/styles';

interface Props {
  testId?: string;
  columnsCount?: number;
  shouldRightAlignLastCol?: boolean;
  width?: string;
  contentWidth?: string;
}

interface BoxContentProps {
  index: number;
  columnsCount: number;
  marginBottom: string;
  shouldRightAlignLastCol?: boolean;
  contentWidth?: string;
}

const renderBoxContent = ({
  index,
  columnsCount,
  marginBottom,
  shouldRightAlignLastCol,
  contentWidth,
}: BoxContentProps) => {
  const isLastRow = index === columnsCount - 1;
  const isRightAligned = shouldRightAlignLastCol && isLastRow;

  return (
    <SkeletonLoader
      width={contentWidth || '15%'}
      marginBottom={marginBottom}
      marginRight={isLastRow ? '0' : '40px'}
      isRightAligned={isRightAligned}
    />
  );
};

/**
 * BoxLoader component is used to render a loader for a box component.
 * Usually used in the order details and invoice details pages.
 * @param testId - testId for the component
 * @param columnsCount - number of columns in the box
 * @param shouldRightAlignLastCol - flag to right align the last column
 * @param width - width of the box (valid CSS unit)
 * @param contentWidth - width of the inline loader (valid CSS unit)
 */
export const BoxLoader = ({
  testId = 'box-loader',
  columnsCount = 3,
  shouldRightAlignLastCol,
  width,
  contentWidth,
}: // eslint-disable-next-line max-params
Props) => {
  const renderContentLoaders = (marginBottom: string) => {
    return (
      <SkeletonLoaderWrapper>
        {Array.from({ length: columnsCount }).map((_, index) =>
          renderBoxContent({ index, columnsCount, marginBottom, shouldRightAlignLastCol, contentWidth })
        )}
      </SkeletonLoaderWrapper>
    );
  };

  return (
    <div data-testid={testId} style={{ width: width }}>
      <Box padding="space.250" xcss={boxStyles}>
        {renderContentLoaders('8px')}
        {renderContentLoaders('0')}
      </Box>
    </div>
  );
};
